import { useEffect, useRef, useState, type FormEvent } from 'react';

import { adminApiMessage } from '../adminApi';
import { formatDate } from '../format';
import { StatusBadge } from '../components/StatusBadge';
import {
  capabilityLabels,
  checkStepText,
  checkSummary,
  checksFromError,
  choiceDrafts,
  dollars,
  fallbackText,
  filterCatalog,
  modelsApi,
  NO_PRIVACY_RULE_DETAIL,
  NO_PRIVACY_RULE_TEXT,
  PAGE_PICTURES_UNSEEN_TEXT,
  pagePicturesUnseenBy,
  PICTURE_PRICE_HINT,
  PICTURE_PRICE_TEXT,
  priceSourceText,
  priceText,
  providerName,
  resetText,
  sameChoices,
  seesAttachments,
  sourceText,
  suggestAlias,
  tokens,
  viaText,
  type CatalogFilter,
  type ChoiceDraft,
  type ModelCheckResult,
  type ModelRole,
  type ModelRoleView,
  type ModelView,
  type ModelsView,
} from '../models';
import { GuideLink } from '../evals/GuideLink';
import '../../styles/admin-models.css';

// Every model Neomaris uses, chosen here and stored in the database (TOM-465,
// ADR 0322). A role set here wins over its deploy variable; "Use …" hands it
// back. A save runs a live check of the model through the role's own call path
// first, and nothing changes unless it passes. The /model list owners pick from
// is edited below the roles.

const SOURCE_LABEL: Record<string, string> = {
  anthropic_models: "Anthropic's models",
  anthropic_prices: "Anthropic's prices",
  openrouter_models: "OpenRouter's catalog",
};

export function ModelName({ m }: { m: ModelView }) {
  return (
    <div className="models-name">
      <span className="models-label">{m.label}</span>
      <span className="models-via">{viaText(m)}</span>
      <code className="models-id">{m.id}</code>
      <span className="models-chips">
        {m.listing === 'delisted' ? (
          <StatusBadge status="failed" label="no longer listed" />
        ) : m.listing === 'unknown' ? (
          <StatusBadge status="suggested" label="not in catalog" />
        ) : null}
        {!m.configured ? (
          <StatusBadge status="failed" label={`no ${providerName(m.provider)} key`} />
        ) : null}
        {m.noPrivacyRule ? <StatusBadge status="suggested" label={NO_PRIVACY_RULE_TEXT} /> : null}
      </span>
    </div>
  );
}

/** The plain warning under a model whose calls carry no privacy rule (TOM-657). */
function NoPrivacyRuleNote({ testId }: { testId?: string }) {
  return (
    <p className="models-warn" data-testid={testId}>
      {NO_PRIVACY_RULE_DETAIL}
    </p>
  );
}

function Price({ m, pictures = false }: { m: ModelView; pictures?: boolean }) {
  // A model drawing Neo's pictures is billed by the picture (TOM-641).
  if (pictures) {
    return (
      <div className="models-price">
        <span>{PICTURE_PRICE_TEXT}</span>
        <span className="models-muted">{PICTURE_PRICE_HINT}</span>
      </div>
    );
  }
  const source = priceSourceText(m);
  return (
    <div className="models-price">
      <span>{priceText(m)}</span>
      {source !== null ? <span className="models-muted">from {source}</span> : null}
    </div>
  );
}

function CheckView({ results }: { results: ModelCheckResult[] }) {
  return (
    <ul className="models-checks" data-testid="models-check-results">
      {results.map((r) => (
        <li key={r.model} className={r.ok ? 'models-check-ok' : 'models-check-bad'}>
          <span>{checkSummary(r)}</span>
          {r.steps.length > 0 ? (
            <span className="models-muted">{r.steps.map(checkStepText).join(' · ')}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** Search the catalog for models a role can use, or take an id typed in. */
function ModelPicker({
  role,
  draws = false,
  exclude,
  onPick,
}: {
  role: ModelRole;
  /** The role draws pictures (TOM-641): the catalog offers image models only. */
  draws?: boolean;
  exclude: string[];
  onPick: (m: ModelView | string) => void;
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<{ models: ModelView[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    let ignore = false;
    const t = setTimeout(() => {
      modelsApi
        .catalog(role, q)
        .then((r) => {
          if (!ignore) {
            setResults(r);
            setError(null);
          }
        })
        .catch((err) => {
          if (!ignore) setError(adminApiMessage(err, 'Could not search the catalog.'));
        });
    }, 200);
    return () => {
      ignore = true;
      clearTimeout(t);
    };
  }, [role, q]);

  function useTyped(e: FormEvent) {
    e.preventDefault();
    if (typed.trim().length > 0) onPick(typed.trim());
  }

  const shown = (results?.models ?? []).filter((m) => !exclude.includes(m.id));
  return (
    <div className="models-picker">
      <input
        type="search"
        className="models-search"
        placeholder={
          draws
            ? 'Search image models: gemini, image…'
            : 'Search models: haiku, opus, deepseek, gpt…'
        }
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="Search the model catalog"
        data-testid="models-picker-search"
      />
      {error !== null ? <p className="admin-inline-error">{error}</p> : null}
      <ul className="models-results" data-testid="models-picker-results">
        {results === null ? <li className="models-muted">Loading…</li> : null}
        {results !== null && shown.length === 0 ? (
          <li className="models-muted">No listed model matches that this role can use.</li>
        ) : null}
        {shown.map((m) => (
          <li key={m.id}>
            <button type="button" className="models-result" onClick={() => onPick(m)}>
              <span className="models-result-name">
                {m.label} <span className="models-via">{viaText(m)}</span>
                {m.noPrivacyRule ? (
                  <>
                    {' '}
                    <StatusBadge status="suggested" label={NO_PRIVACY_RULE_TEXT} />
                  </>
                ) : null}
              </span>
              <code className="models-id">{m.id}</code>
              <span className="models-muted">{draws ? PICTURE_PRICE_TEXT : priceText(m)}</span>
              {m.noPrivacyRule ? (
                <span className="models-result-warn">{NO_PRIVACY_RULE_DETAIL}</span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
      {results !== null && results.total > shown.length ? (
        <p className="models-muted">
          Showing {shown.length} of {results.total}. Search to narrow it down.
        </p>
      ) : null}
      <form className="models-typed" onSubmit={useTyped}>
        <label htmlFor={`models-typed-${role}`}>Or a model id</label>
        <input
          id={`models-typed-${role}`}
          type="text"
          placeholder="anthropic:claude-… or openrouter:vendor/model"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
        <button type="submit" className="admin-button" disabled={typed.trim().length === 0}>
          Use this id
        </button>
      </form>
    </div>
  );
}

/** Pick a role's model (or the judges' list), check it, and save it. */
function RoleEditor({
  role,
  onSaved,
  onClose,
}: {
  role: ModelRoleView;
  onSaved: (view: ModelsView) => void;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<string[]>(
    role.maxModels > 1 ? role.models.map((m) => m.id) : []
  );
  // The picked models with no privacy rule, as the catalog described them
  // (TOM-657); an id typed in is not known until it is saved.
  const [noRule, setNoRule] = useState<string[]>(
    role.models.filter((m) => m.noPrivacyRule).map((m) => m.id)
  );
  const [busy, setBusy] = useState<'check' | 'save' | null>(null);
  const [checks, setChecks] = useState<ModelCheckResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function pick(m: ModelView | string) {
    const id = typeof m === 'string' ? m : m.id;
    if (typeof m !== 'string' && m.noPrivacyRule) {
      setNoRule((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
    setChecks(null);
    setError(null);
    setPicked((prev) =>
      role.maxModels === 1
        ? [id]
        : prev.includes(id)
          ? prev
          : [...prev, id].slice(0, role.maxModels)
    );
  }

  function move(i: number, by: number) {
    setPicked((prev) => {
      const next = [...prev];
      const [x] = next.splice(i, 1);
      next.splice(Math.max(0, Math.min(next.length, i + by)), 0, x!);
      return next;
    });
  }

  async function check() {
    setBusy('check');
    setError(null);
    try {
      setChecks(await Promise.all(picked.map((m) => modelsApi.check(role.role, m))));
    } catch (err) {
      setError(adminApiMessage(err, 'The check could not run.'));
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    setBusy('save');
    setError(null);
    try {
      onSaved(await modelsApi.setRole(role.role, picked));
    } catch (err) {
      const failed = checksFromError(err);
      if (failed !== null) setChecks(failed);
      else setError(adminApiMessage(err, 'The model could not be saved.'));
    } finally {
      setBusy(null);
    }
  }

  const unchanged =
    picked.length === role.models.length &&
    picked.every((id, i) => role.models[i]?.id === id) &&
    role.source === 'admin';
  return (
    <div className="models-editor" data-testid={`models-editor-${role.role}`}>
      {role.maxModels > 1 ? (
        <ol className="models-picked">
          {picked.map((id, i) => (
            <li key={id}>
              <code className="models-id">{id}</code>
              <span className="models-picked-actions">
                <button
                  type="button"
                  className="admin-button-inline"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  Up
                </button>
                <button
                  type="button"
                  className="admin-button-inline"
                  disabled={i === picked.length - 1}
                  onClick={() => move(i, 1)}
                >
                  Down
                </button>
                <button
                  type="button"
                  className="admin-button-inline"
                  onClick={() => setPicked((prev) => prev.filter((x) => x !== id))}
                >
                  Remove
                </button>
              </span>
            </li>
          ))}
          {picked.length === 0 ? <li className="models-muted">No judges chosen.</li> : null}
        </ol>
      ) : picked.length === 1 ? (
        <>
          <p className="models-choice-line">
            New model: <code className="models-id">{picked[0]}</code>
          </p>
          {noRule.includes(picked[0]!) ? (
            <NoPrivacyRuleNote testId={`models-no-privacy-picked-${role.role}`} />
          ) : null}
        </>
      ) : (
        <p className="models-muted">Choose a model below.</p>
      )}
      {picked.length < role.maxModels || role.maxModels === 1 ? (
        <ModelPicker
          role={role.role}
          draws={role.needs.includes('image_output')}
          exclude={picked}
          onPick={pick}
        />
      ) : null}
      {checks !== null ? <CheckView results={checks} /> : null}
      {busy !== null ? (
        <p className="models-busy" role="status">
          {busy === 'check' ? 'Checking' : 'Checking, then saving'} — a real call through this
          role&apos;s path. It can take up to a minute.
        </p>
      ) : null}
      {error !== null ? <p className="admin-inline-error">{error}</p> : null}
      <div className="models-editor-actions">
        <button
          type="button"
          className="admin-button"
          disabled={picked.length === 0 || busy !== null}
          onClick={() => void check()}
        >
          Check
        </button>
        <button
          type="button"
          className="admin-button-affirm"
          disabled={picked.length === 0 || busy !== null || unchanged}
          onClick={() => void save()}
          data-testid={`models-save-${role.role}`}
        >
          Check and save
        </button>
        <button type="button" className="admin-button" disabled={busy !== null} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function RoleRow({
  role,
  roles,
  editing,
  onEdit,
  onClose,
  onSaved,
}: {
  role: ModelRoleView;
  roles: ModelRoleView[];
  editing: boolean;
  onEdit: () => void;
  onClose: () => void;
  onSaved: (view: ModelsView) => void;
}) {
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reset() {
    setResetting(true);
    setError(null);
    try {
      onSaved(await modelsApi.resetRole(role.role));
    } catch (err) {
      setError(adminApiMessage(err, 'Could not go back to the default.'));
    } finally {
      setResetting(false);
    }
  }

  return (
    <article className="models-role" data-testid={`models-role-${role.role}`}>
      <header className="models-role-head">
        <div>
          <h3>{role.label}</h3>
          <p className="models-muted">{role.description}</p>
        </div>
        <div className="models-role-actions">
          {!editing ? (
            <button
              type="button"
              className="admin-button"
              onClick={onEdit}
              data-testid={`models-change-${role.role}`}
            >
              Change
            </button>
          ) : null}
          {role.source === 'admin' && !editing ? (
            <button
              type="button"
              className="admin-button"
              disabled={resetting}
              onClick={() => void reset()}
            >
              {resetText(role, roles)}
            </button>
          ) : null}
        </div>
      </header>
      <ol className="models-role-models">
        {role.models.map((m) => (
          <li key={m.id} className="models-role-model">
            <ModelName m={m} />
            <Price m={m} pictures={role.needs.includes('image_output')} />
          </li>
        ))}
      </ol>
      {role.models.some((m) => m.noPrivacyRule) ? (
        <NoPrivacyRuleNote testId={`models-no-privacy-${role.role}`} />
      ) : null}
      <p className="models-source">
        <StatusBadge
          status={role.source === 'admin' ? 'published' : 'running'}
          label={sourceText(role, roles)}
        />
        {role.source === 'admin' && role.updatedAt !== null ? (
          <span className="models-muted">
            {' '}
            by {role.updatedBy ?? 'a former admin'} on {formatDate(role.updatedAt)}
          </span>
        ) : null}
      </p>
      {role.source === 'admin' ? (
        <p className="models-fallback" data-testid={`models-fallback-${role.role}`}>
          {fallbackText(role, roles)}
        </p>
      ) : null}
      {error !== null ? <p className="admin-inline-error">{error}</p> : null}
      {editing ? <RoleEditor role={role} onSaved={onSaved} onClose={onClose} /> : null}
    </article>
  );
}

/** The /model list owners pick from. */
function ChoicesSection({
  view,
  onSaved,
}: {
  view: ModelsView;
  onSaved: (view: ModelsView) => void;
}) {
  const saved = choiceDrafts(view.choices);
  const [drafts, setDrafts] = useState<ChoiceDraft[]>(saved);
  // Models picked from the catalog in this edit, so a new row shows its name.
  const [picked, setPicked] = useState<Map<string, ModelView>>(new Map());
  const [adding, setAdding] = useState(false);
  // The row whose model is being changed, with the picker open under it.
  const [changing, setChanging] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [checks, setChecks] = useState<ModelCheckResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setDrafts(choiceDrafts(view.choices)), [view.choices]);

  const byId = new Map([...picked, ...view.choices.map((c) => [c.model.id, c.model] as const)]);
  const savedIds = new Set(view.choices.map((c) => c.model.id));
  const dirty = !sameChoices(drafts, saved);
  // The seeded list reads from the table but no admin has changed it yet.
  const edited = view.choicesSource === 'admin' && view.choicesUpdatedBy !== null;

  function update(i: number, patch: Partial<ChoiceDraft>) {
    setDrafts((prev) => prev.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  }

  function move(i: number, by: number) {
    setDrafts((prev) => {
      const next = [...prev];
      const [x] = next.splice(i, 1);
      next.splice(Math.max(0, Math.min(next.length, i + by)), 0, x!);
      return next;
    });
  }

  function add(m: ModelView | string) {
    const model = typeof m === 'string' ? m : m.id;
    const taken = drafts.map((d) => d.alias);
    const alias =
      typeof m === 'string'
        ? suggestAlias({ modelId: m.split(':').pop() ?? m }, taken)
        : suggestAlias(m, taken);
    if (typeof m !== 'string') setPicked((prev) => new Map(prev).set(m.id, m));
    setDrafts((prev) => [
      ...prev,
      {
        alias,
        model,
        label: typeof m === 'string' ? model : m.label,
        // Charts are turned on from the eval's chart cases, never assumed.
        graphics: false,
        vision: typeof m === 'string' ? false : seesAttachments(m),
      },
    ]);
    setAdding(false);
    setChecks(null);
  }

  /**
   * Put another model on a row. The alias stays; the label and photos follow
   * the new model, and charts start off until the eval's chart cases pass.
   */
  function changeModel(i: number, m: ModelView | string) {
    if (typeof m !== 'string') setPicked((prev) => new Map(prev).set(m.id, m));
    update(i, {
      model: typeof m === 'string' ? m : m.id,
      label: typeof m === 'string' ? m : m.label,
      graphics: false,
      vision: typeof m === 'string' ? false : seesAttachments(m),
    });
    setChanging(null);
    setChecks(null);
  }

  async function save() {
    setBusy(true);
    setError(null);
    setChecks(null);
    setChanging(null);
    try {
      onSaved(await modelsApi.setChoices(drafts));
    } catch (err) {
      const failed = checksFromError(err);
      if (failed !== null) setChecks(failed);
      else setError(adminApiMessage(err, 'The list could not be saved.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="models-card" data-testid="models-choices">
      <header className="models-card-head">
        <div>
          <h2>Owner choices (/model)</h2>
          <p className="models-muted">
            What owners can switch Neo to with <code>/model</code>. Every owner sees this list. Turn
            on charts once the model passes the eval&apos;s chart cases; photos only for a model
            that can see images and PDFs.
          </p>
        </div>
        <StatusBadge
          status={edited ? 'published' : 'running'}
          label={edited ? 'Set here' : 'Built-in list'}
        />
      </header>
      <ol className="models-choice-list">
        {drafts.map((d, i) => {
          const m = byId.get(d.model);
          return (
            <li
              key={`${d.model}-${i}`}
              className="models-choice"
              data-testid={`models-choice-${i}`}
            >
              <div className="models-choice-fields">
                <label className="models-field">
                  <span>/model</span>
                  <input
                    className="models-alias"
                    value={d.alias}
                    onChange={(e) => update(i, { alias: e.target.value.toLowerCase() })}
                  />
                </label>
                <label className="models-field models-field-wide">
                  <span>Label</span>
                  <input value={d.label} onChange={(e) => update(i, { label: e.target.value })} />
                </label>
              </div>
              <div className="models-choice-model">
                {m !== undefined ? (
                  <ModelName m={m} />
                ) : (
                  <code className="models-id">{d.model}</code>
                )}
                {!savedIds.has(d.model) ? (
                  <span className="models-muted">New: checked as Neo when you save.</span>
                ) : null}
                {changing !== i ? (
                  <button
                    type="button"
                    className="admin-button-inline"
                    onClick={() => {
                      setChanging(i);
                      setAdding(false);
                    }}
                    data-testid={`models-choice-change-${i}`}
                  >
                    Change model
                  </button>
                ) : null}
              </div>
              {changing === i ? (
                <div className="models-editor">
                  <ModelPicker
                    role="neo_chat"
                    exclude={drafts.map((x) => x.model)}
                    onPick={(picked) => changeModel(i, picked)}
                  />
                  <div className="models-editor-actions">
                    <button
                      type="button"
                      className="admin-button"
                      onClick={() => setChanging(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
              <div className="models-choice-flags">
                <label>
                  <input
                    type="checkbox"
                    checked={d.graphics}
                    onChange={(e) => update(i, { graphics: e.target.checked })}
                  />{' '}
                  Charts
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={d.vision}
                    onChange={(e) => update(i, { vision: e.target.checked })}
                  />{' '}
                  Photos
                </label>
                <span className="models-choice-order">
                  <button
                    type="button"
                    className="admin-button-inline"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    className="admin-button-inline"
                    disabled={i === drafts.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    className="admin-button-inline"
                    disabled={drafts.length === 1}
                    onClick={() => setDrafts((prev) => prev.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                </span>
              </div>
            </li>
          );
        })}
      </ol>
      {adding ? (
        <div className="models-editor">
          <ModelPicker role="neo_chat" exclude={drafts.map((d) => d.model)} onPick={add} />
          <div className="models-editor-actions">
            <button type="button" className="admin-button" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {checks !== null ? <CheckView results={checks} /> : null}
      {busy ? (
        <p className="models-busy" role="status">
          Checking each new model through Neo&apos;s tool loop, then saving. It can take up to a
          minute.
        </p>
      ) : null}
      {error !== null ? <p className="admin-inline-error">{error}</p> : null}
      <div className="models-editor-actions">
        {!adding ? (
          <button
            type="button"
            className="admin-button"
            onClick={() => {
              setAdding(true);
              setChanging(null);
            }}
            data-testid="models-choices-add"
          >
            Add a model
          </button>
        ) : null}
        <button
          type="button"
          className="admin-button-affirm"
          disabled={!dirty || busy}
          onClick={() => void save()}
          data-testid="models-choices-save"
        >
          Save the list
        </button>
        {dirty ? (
          <button
            type="button"
            className="admin-button"
            disabled={busy}
            onClick={() => {
              setDrafts(saved);
              setChanging(null);
            }}
          >
            Discard changes
          </button>
        ) : null}
      </div>
      {edited && view.choicesUpdatedAt !== null ? (
        <p className="models-muted">
          Last changed by {view.choicesUpdatedBy} on {formatDate(view.choicesUpdatedAt)}.
        </p>
      ) : null}
    </section>
  );
}

/** USD per million tokens, input · output, short. */
function pricePair(p: { input: number; output: number }): string {
  return `${dollars(p.input)} · ${dollars(p.output)}`;
}

/** The whole catalog, searchable and filterable, in a pop-up over the page. */
function CatalogDialog({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [models, setModels] = useState<ModelView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CatalogFilter>({
    q: '',
    provider: 'all',
    showDelisted: false,
  });

  useEffect(() => {
    const dialog = ref.current;
    if (dialog !== null && !dialog.open) dialog.showModal();
    // showModal focuses the first control (Close); start in the search box.
    searchRef.current?.focus();
    let ignore = false;
    modelsApi
      .catalogAll()
      .then((r) => {
        if (!ignore) setModels(r.models);
      })
      .catch((err) => {
        if (!ignore) setError(adminApiMessage(err, 'Could not load the catalog.'));
      });
    return () => {
      ignore = true;
    };
  }, []);

  const shown = models === null ? [] : filterCatalog(models, filter);
  const delisted = models?.filter((m) => m.listing === 'delisted').length ?? 0;
  return (
    <dialog
      ref={ref}
      className="models-dialog"
      aria-labelledby="models-dialog-title"
      onClose={onClose}
      // A click on the backdrop lands on the dialog itself; inside, on its body.
      onClick={(e) => {
        if (e.target === e.currentTarget) ref.current?.close();
      }}
      data-testid="models-catalog-dialog"
    >
      <div className="models-dialog-body">
        <header className="models-dialog-head">
          <h2 id="models-dialog-title">Model catalog</h2>
          <button type="button" className="admin-button" onClick={() => ref.current?.close()}>
            Close
          </button>
        </header>
        <div className="models-dialog-filters">
          <input
            type="search"
            className="models-search"
            placeholder="Search by name or id"
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            aria-label="Search the catalog"
            ref={searchRef}
          />
          <select
            className="models-select"
            value={filter.provider}
            onChange={(e) =>
              setFilter((f) => ({ ...f, provider: e.target.value as CatalogFilter['provider'] }))
            }
            aria-label="Provider"
          >
            <option value="all">All providers</option>
            <option value="anthropic">Anthropic</option>
            <option value="openrouter">OpenRouter</option>
          </select>
          {delisted > 0 ? (
            <label className="models-dialog-toggle">
              <input
                type="checkbox"
                checked={filter.showDelisted}
                onChange={(e) => setFilter((f) => ({ ...f, showDelisted: e.target.checked }))}
              />{' '}
              No longer listed ({delisted})
            </label>
          ) : null}
        </div>
        <p className="models-muted">
          {models === null
            ? error === null
              ? 'Loading…'
              : ''
            : `${shown.length} of ${models.length} models. Prices are USD per million tokens, input · output.`}
        </p>
        {error !== null ? <p className="admin-inline-error">{error}</p> : null}
        <div className="models-dialog-scroll">
          <table className="admin-table models-catalog-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Price</th>
                <th>Context</th>
                <th>Can</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((m) => (
                <tr
                  key={m.id}
                  className={m.listing === 'delisted' ? 'models-row-delisted' : undefined}
                >
                  <td>
                    <div className="models-label">{m.label}</div>
                    <code className="models-id">{m.id}</code>
                    {m.noPrivacyRule ? (
                      <div>
                        <StatusBadge status="suggested" label={NO_PRIVACY_RULE_TEXT} />
                      </div>
                    ) : null}
                    {m.listing === 'delisted' ? (
                      <div>
                        <StatusBadge
                          status="failed"
                          label={`no longer listed${m.delistedAt !== null ? ` since ${formatDate(m.delistedAt)}` : ''}`}
                        />
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {m.prices !== null ? (
                      <>
                        <div>{pricePair(m.prices)}</div>
                        {m.prices.longPrompt !== null ? (
                          <div className="models-muted">
                            over {tokens(m.prices.longPrompt.overPromptTokens)}:{' '}
                            {pricePair(m.prices.longPrompt)}
                          </div>
                        ) : null}
                        <div className="models-muted">{viaText(m)}</div>
                      </>
                    ) : (
                      <span className="models-muted">No price</span>
                    )}
                  </td>
                  <td>
                    {m.contextTokens !== null ? tokens(m.contextTokens) : '—'}
                    {m.maxOutputTokens !== null ? (
                      <div className="models-muted">{tokens(m.maxOutputTokens)} out</div>
                    ) : null}
                  </td>
                  <td>
                    <span className="models-chips">
                      {capabilityLabels(m).map((c) => (
                        <span key={c} className="models-cap">
                          {c}
                        </span>
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </dialog>
  );
}

function CatalogStatus({ view, onRefreshed }: { view: ModelsView; onRefreshed: () => void }) {
  const [state, setState] = useState<'idle' | 'queued' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [viewing, setViewing] = useState(false);

  async function refresh() {
    setState('queued');
    setMessage(null);
    try {
      await modelsApi.refreshCatalog();
      setMessage('Refreshing: the list updates here in a few seconds.');
      // The refresh is a job; read the page again once it has had time to run.
      setTimeout(() => {
        onRefreshed();
        setState('idle');
        setMessage(null);
      }, 8_000);
    } catch (err) {
      setState('error');
      setMessage(adminApiMessage(err, 'Could not start a refresh.'));
    }
  }

  const failing = view.catalog.sources.filter((s) => s.lastError !== null);
  const newest = view.catalog.sources
    .map((s) => s.lastSuccessAt)
    .filter((s): s is string => s !== null)
    .sort()
    .pop();
  return (
    <section className="models-card models-catalog" data-testid="models-catalog">
      <div>
        <h2>Catalog</h2>
        <p className="models-muted">
          {view.catalog.counts.anthropic} Claude models and {view.catalog.counts.openrouter}{' '}
          OpenRouter models.{' '}
          {newest !== undefined ? `Last refreshed ${formatDate(newest)}.` : 'Never refreshed.'}{' '}
          Refreshed daily; Claude prices come from Anthropic&apos;s own price list.
        </p>
        {failing.map((s) => (
          <p key={s.source} className="models-warn">
            {SOURCE_LABEL[s.source] ?? s.source}: {s.lastError}
          </p>
        ))}
        {message !== null ? (
          <p className={state === 'error' ? 'admin-inline-error' : 'models-muted'}>{message}</p>
        ) : null}
      </div>
      <div className="models-catalog-actions">
        <button
          type="button"
          className="admin-button"
          onClick={() => setViewing(true)}
          data-testid="models-catalog-view"
        >
          View catalog
        </button>
        <button
          type="button"
          className="admin-button"
          disabled={state === 'queued'}
          onClick={() => void refresh()}
        >
          Refresh now
        </button>
      </div>
      {viewing ? <CatalogDialog onClose={() => setViewing(false)} /> : null}
    </section>
  );
}

export function AdminModels() {
  const [view, setView] = useState<ModelsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ModelRole | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let ignore = false;
    modelsApi
      .view()
      .then((v) => {
        if (!ignore) setView(v);
      })
      .catch((err) => {
        if (!ignore) setError(adminApiMessage(err, 'Failed to load the models.'));
      });
    return () => {
      ignore = true;
    };
  }, [reloads]);

  function saved(next: ModelsView) {
    setView(next);
    setEditing(null);
  }

  const delisted =
    view?.roles.flatMap((r) => r.models).filter((m) => m.listing === 'delisted') ?? [];
  // The board editor sees a picture of each page it writes only on an
  // Anthropic model that takes images (ADR 0393 §12.3, TOM-665).
  const unseen = view === null ? null : pagePicturesUnseenBy(view);
  return (
    <section className="admin-section models-page">
      <header className="admin-section-header">
        <h1>Models</h1>
        <span className="admin-count">{view === null ? '…' : `${view.roles.length} roles`}</span>
        <GuideLink section="models" />
      </header>
      <p className="admin-hint">
        Every model Neomaris uses. A model set here wins over its deploy variable, and every save
        checks the model with a real call through that role&apos;s path first. Jev, Neo&apos;s input
        gate, and the embeddings model are not chosen here.
      </p>
      {error !== null ? <p className="admin-inline-error">{error}</p> : null}
      {view === null && error === null ? <p className="models-muted">Loading…</p> : null}
      {view !== null ? (
        <>
          {delisted.length > 0 ? (
            <div className="admin-auth-banner models-alert" role="alert">
              {delisted.map((m) => m.id).join(', ')} {delisted.length === 1 ? 'is' : 'are'} no
              longer listed by {delisted.length === 1 ? 'its provider' : 'their providers'}. Choose
              a replacement before calls start failing.
            </div>
          ) : null}
          {unseen !== null ? (
            <p className="models-warn" data-testid="models-page-pictures-warn">
              The board editor runs on {unseen.label}. {PAGE_PICTURES_UNSEEN_TEXT}: only a Claude
              model that takes images sees them.
            </p>
          ) : null}
          {!view.keys.anthropic || !view.keys.openrouter ? (
            <p className="models-warn">
              {!view.keys.anthropic
                ? 'ANTHROPIC_API_KEY is not set here: Claude models cannot be used. '
                : ''}
              {!view.keys.openrouter
                ? 'OPENROUTER_API_KEY is not set here: OpenRouter models cannot be used.'
                : ''}
            </p>
          ) : null}
          <CatalogStatus view={view} onRefreshed={() => setReloads((n) => n + 1)} />
          <section className="models-card" data-testid="models-roles">
            <header className="models-card-head">
              <h2>System models</h2>
            </header>
            <div className="models-roles">
              {view.roles.map((r) => (
                <RoleRow
                  key={r.role}
                  role={r}
                  roles={view.roles}
                  editing={editing === r.role}
                  onEdit={() => setEditing(r.role)}
                  onClose={() => setEditing(null)}
                  onSaved={saved}
                />
              ))}
            </div>
          </section>
          <ChoicesSection view={view} onSaved={saved} />
        </>
      ) : null}
    </section>
  );
}
